<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.atmosphere-condition.troposphereDelaysChart"
					descriptionKey="stats.atmosphere-condition.troposphereDelaysChartTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<TimeSeriesChart v-if="mountpointsSelected.length > 0" :isLoading="isLoading" :series="series" :options="chartOptions" :height="500" />
				<EmptyChartFiltersMessage v-else messageKey="stats.atmosphere-condition.baseStationsPlaceholder" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<BaseStationSelect v-model="mountpointsSelected" :onUpdate="fetchStatsItems" subNetworkFilter />
						<DateRangeChooser v-model="datesRange" @change="fetchStatsItems" />
						<VerticalLayout v-if="isAggregated" class="pd-vertical">
							<LabeledSwitch v-model="showMin" :rightLabel="$t('controls.switches.showMin')" />
							<LabeledSwitch v-model="showMax" :rightLabel="$t('controls.switches.showMax')" />
						</VerticalLayout>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { Row, Col, Card } from 'ant-design-vue';

import Api from '@/api/Api';

import { useSessionStore } from '@/stores/session';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter } from '@/utils/chartsUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';

import DateRangeChooser from '@/components/common/DateRangeChooser';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';


const getMountpointSeriesKey = (period) => item => {
	if (period > 1 && item.agg_function) {
		return item.mountpoint + '-' + item.agg_function;
	}
	return item.mountpoint;
};
const getValue = item => item.final_value / 1_000_000;

export default {
	components: {
		Row, Col, Card,
		VerticalLayout, DateRangeChooser, TimeSeriesChart, EmptyChartFiltersMessage,
		BaseStationSelect, LabeledSwitch, StatsChartTitleWithDescription,
	},
	setup() {
		const sessionStore = useSessionStore();
		const subNetworksStore = useSubNetworksStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const mountpointsSelected = ref([]);
		const showMin = ref(true);
		const showMax = ref(true);

		const { apiData, isLoading, doRequest } = Api.useTroposphereStatsAggregatedGet();
		const isAggregated = computed(() => apiData.value.period > 1);

		const fetchStatsItems = () => {
			if (datesRangeStr.value != null && mountpointsSelected.value.length > 0) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
					mountpoints: mountpointsSelected.value,
					sub_network_id: subNetworksStore.subNetworkSelectedId,
					show_min: showMin.value,
					show_max: showMax.value,
				});
			}
		};

		watch([showMin, showMax], () => {
			fetchStatsItems();
		});
		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			fetchStatsItems();
		});

		const series = computed(() => generateSeries({
			items: apiData.value.statsItems.filter(item => item.kind === 'troposphereDelay'),
			getSeriesKeyFn: getMountpointSeriesKey(apiData.value.period),
			getValueFn: getValue,
			seriesStep: apiData.value.period,
		}).series);

		const chartOptions = computed(() => {
			let options = getBaseOptions('troposphereAggregated', series.value, apiData.value.funcCount);
			options.yaxis.title.text = 'metres';
			options.yaxis.decimalsInFloat = 6;
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(6) + ' m');
			return options;
		});

		return {
			isLoading,
			datesRange,
			mountpointsSelected,
			showMin,
			showMax,
			isAggregated,
			series,
			chartOptions,
			fetchStatsItems,
		};
	},
}
</script>
